import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { FormattedMessage } from 'react-intl';

import { FlexRow, Gap } from 'ui-components/components/flex';
import { Tabs, TabList, Tab, TabPanel } from 'ui-components/components/tabs';

import classnames from 'clsx';

import { messages } from './messages';
import styles from './plan-grid-tabs.module.less';
import { PlanGridLoader } from '../plan-grid-loader';
import { PlanGridConfig, useManageSubscriptionDrawerSectionVisibility } from '../use-manage-subscription-drawer-section-visibility';
import { useManageSubscriptionDrawerContext } from '../../../context/manage-subscription-drawer-context';
import { useEnableSimplifiedPlanGrid } from '../../../../../../feature-flags/hooks/use-enable-simplified-plan-grid';
import { PageEvents, useTrackEvent } from '../../../hooks/use-track-event';
import Global from '../../../../../../store/global-selectors';
import { getBasePlanForNonWalletAccessPlan } from '../../../../../../hooks/non-wallet-access-hooks/subscription-page-display-hooks';

const PLANS_TAB_INDEX = 0;

const legacyTabPanelMergedClassName = classnames(styles['tab-panel'], styles['tab-panel-not-first']);

/**
 * @param {object} props
 * @param {(billingPlanId: string) => void} props.onSelectPlan
 * @returns {React.JSX.Element}
 */
export const PlanGridTabs = ({ onSelectPlan }) => {
  const { setIsMigratingToNewestPlans } = useManageSubscriptionDrawerContext();
  const trackTabSwitch = useTrackEvent(PageEvents.SUBSCRIPTION_MIGRATION_TAB_SWITCH);
  const { planGridConfig } = useManageSubscriptionDrawerSectionVisibility();
  const isSimplifiedPlanGridEnabled = useEnableSimplifiedPlanGrid();
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const { currentBillingPlan, planToNonWalletAccessPlanMap, allPlans } = subscriptionData ?? {};

  const legacyPlanGridConfig = isSimplifiedPlanGridEnabled ? PlanGridConfig.SimplifiedLegacy : PlanGridConfig.StandardLegacy;

  const handleTabChange = useCallback(
    (index) => {
      const isMigratingTab = index === PLANS_TAB_INDEX;
      setIsMigratingToNewestPlans(isMigratingTab);
      trackTabSwitch({ tabName: isMigratingTab ? 'plans' : 'legacy_plans' });

      // When switching to legacy tab, select the BASE plan (not BYOA)
      // This ensures plan summary shows correct pricing (e.g., $99.99 + $40 BYOA separately)
      if (!isMigratingTab && currentBillingPlan && allPlans) {
        const basePlan = getBasePlanForNonWalletAccessPlan(currentBillingPlan, planToNonWalletAccessPlanMap, allPlans);
        onSelectPlan(basePlan.billingPlanId);
      }
    },
    [setIsMigratingToNewestPlans, trackTabSwitch, currentBillingPlan, planToNonWalletAccessPlanMap, allPlans, onSelectPlan],
  );

  return (
    <Tabs className={styles['plan-grid-tabs']} defaultIndex={PLANS_TAB_INDEX} onSelectedTabIndexChange={handleTabChange}>
      <TabList className={styles['tab-list']}>
        <Tab>
          <FlexRow gap={Gap.Space8}>
            <FontAwesomeIcon className={styles['promo-icon']} icon={faBolt} />
            <FormattedMessage {...messages.plansTab} />
          </FlexRow>
        </Tab>
        <Tab>
          <FormattedMessage {...messages.legacyPlansTab} />
        </Tab>
      </TabList>
      <TabPanel selectedClassName={styles['tab-panel']}>
        <PlanGridLoader isMergedWithTabs onSelectPlan={onSelectPlan} planGridConfig={planGridConfig} />
      </TabPanel>
      <TabPanel selectedClassName={legacyTabPanelMergedClassName}>
        <PlanGridLoader
          legacyGridClassName={styles['no-section-border-top']}
          onSelectPlan={onSelectPlan}
          planGridConfig={legacyPlanGridConfig}
        />
      </TabPanel>
    </Tabs>
  );
};
